/**
 * One-time lift of a user's existing browser-stored preferences into the
 * server blob, so nobody loses a dashboard they arranged.
 *
 * Runs only when the server blob for this connection is empty, so it can never
 * overwrite preferences set on another machine.
 */

/** Prefixes owned by the preferences system. Tokens, theme, language and the
 *  selected connection are deliberately absent — they are needed before a
 *  session exists and stay in the browser.
 *
 *  Prefixes confirmed against the fourteen stores' actual key helpers/literals
 *  (brief aliases `density` / `theme_recent_colors` / `config_menu_nav` were
 *  wrong or invented — see task-3-report). */
export const LEGACY_KEY_PREFIXES: readonly string[] = [
  'dashboard_layout',
  'app_tabs',
  'list_saved_views',
  'list_column_prefs',
  'list_view_prefs',
  'tabular_prefs',
  'sidebar-pins',
  'command_recents',
  'designer_layout',
  'ui_density',
  'theme-editor-recent-colors',
  'dashboard_tv_mode',
];

function isOwned(key: string): boolean {
  return LEGACY_KEY_PREFIXES.some((p) => key === p || key.startsWith(p + ':'));
}

/** A key is for another connection when it is scoped and the scope is not ours. */
function belongsToOtherConnection(key: string, connectionId: string): boolean {
  const parts = key.split(':');
  if (parts.length < 2) return false;
  return !parts.includes(connectionId);
}

/** Collect owned keys for this connection. Does not delete — the Gate
 *  clears only after a successful first-run save. */
export function importLegacyPreferences(connectionId: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (!key || !isOwned(key)) continue;
    if (belongsToOtherConnection(key, connectionId)) continue;
    const value = localStorage.getItem(key);
    if (value === null) continue;
    out[key] = value;
  }
  return out;
}

/** Drop keys that were imported and then written to the server. */
export function clearLegacyPreferences(keys: readonly string[]): void {
  keys.forEach((k) => localStorage.removeItem(k));
}
